import { Component, OnInit, ViewChild, Input } from '@angular/core';
import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { AuthenticationService } from 'src/app/services/authentication/authentication.service';
import { EventsService } from 'src/app/services/event/event.service';
import { UserSessionService } from 'src/app/services/user-session/user-session.service';
import { Navigator } from 'src/app/components/menu/navigator';
import { SuccessMessageService } from 'src/app/services/message-provider/success-message.service';
import { LoginService } from 'src/app/services/login/login.service';
import { BiometricService } from 'src/app/services/biometric/biometric.service';
import { ConfigService } from 'src/app/services/config/config.service';
import { UniqueIDService } from 'src/app/services/unique-id/unique-id.service';
import { Platform, AlertController, IonContent, ToastController } from '@ionic/angular';
import { AppLinksService } from 'src/app/services/applinks/app-links.service';
import { GlobalErrorHandlerService } from 'src/app/services/global-errors/global-error-handler.service';
import Cookies from "js-cookie";
import { AppstorageTsService } from 'src/app/services/appstorage/appstorage.ts.service';
import { isUndefined } from 'src/app/services/codeUtils';
import { gsap } from 'gsap';
import { RewardsService } from 'src/app/services/rewards/rewards.service';
import { GalleryService } from 'src/app/services/gallery/gallery.service';
import { ProgressingComponent } from 'src/app/components/progressing/progressing.component';
import { SocialAuthService } from 'src/app/services/authentication/social-auth.service';

@Component({
	selector: 'app-login',
	templateUrl: './login.component.html',
	styleUrls: ['./login.component.scss'],
})

export class LoginComponent implements OnInit {

	@ViewChild(IonContent, { static: false }) content: IonContent;
	@ViewChild(ProgressingComponent) progressingUI!: ProgressingComponent;
	@Input() biometricVerificationFailed = false;
	@Input() redirectedAccount: boolean = false;

	protected showCloseButton: boolean = false;
	public appleAuthEnabled: boolean = false;
	public buttonDisabled: boolean = false;
	public deviceId: any;
	public doLoginInProgress: boolean = false;
	public email: string;
	public emailisSent: boolean = false;
	public error: GlobalErrorHandlerService = null;
	public fbAuthEnabled: boolean = false;
	public googleAuthEnabled: boolean = false;
	public isPlayerFirstVisit: boolean = null;
	public isPlayerLoggedInAsGuest: boolean = false;
	public maxEmailCharacters: number;
	public playAsGuestEnabled: boolean = false;
	public platformID;
	public showCardNotFound: boolean;
	public showDeviceId: boolean = false;
	public showPassword: boolean;
	public userEnteredEmail: boolean = false;
	public userEnteredBoydCard: boolean = false;
	public loginResponse: any;
	public tooManyLoginAttemps: boolean = false;
	public boydLoginURL: any;
	public loginForm: FormGroup;
	public clientapp = new FormControl('')
	public csrf = new FormControl('');
	public password = new FormControl('');
	public tapCount: number = 0;
	public tapTimeoutID: any = 0;
	public btnSendEmailDisabled: boolean = false;

	public username = new FormControl('', [
		Validators.required,
		this.emailOrBoydCardNumberValidator(),
		Validators.maxLength(250),
		Validators.minLength(2)
	]);

	constructor(
		private readonly events: EventsService,
		private readonly socialAuth: SocialAuthService,
		private appStorageService: AppstorageTsService,
		private globalErrorHandlerService: GlobalErrorHandlerService,
		private rewardService: RewardsService,
		private authenticationService: AuthenticationService,
		private userSessionService: UserSessionService,
		private galleryService: GalleryService,
		private loginService: LoginService,
		private biometricService: BiometricService,
		private eventsService: EventsService,
		private configService: ConfigService,
		private navigator: Navigator,
		private uniqueID: UniqueIDService,
		public alertController: AlertController,
		public platform: Platform,
		public successMessageService: SuccessMessageService,
		public appLinksService: AppLinksService,
		public toastController: ToastController
	) { }

	ngOnInit() {

		this.animateComponent();

		this.playAsGuestEnabled = this.socialAuth?.playAsGuestEnabled;
		this.appleAuthEnabled = this.socialAuth?.appleAuthEnabled;
		this.fbAuthEnabled = this.socialAuth?.fbAuthEnabled;
		this.googleAuthEnabled = this.socialAuth?.googleAuthEnabled;

		this.loginForm = new FormGroup({
			csrf: this.csrf,
			username: this.username,
			password: this.password,
			clientapp: this.clientapp,
		});

		this.getDeviceId();
		this.generateCsrfToken();
		this.isFirstTimeAppLaunch();
		this.boydLoginURL = this.configService.getConfig().FEATURE_BOYD_LOGIN_URL ? this.configService.getConfig().FEATURE_BOYD_LOGIN_URL : 'https://rewards.boydgaming.com/'
		if (this.platform.is('capacitor') && !this.biometricVerificationFailed)
			this.initiateBiometric();
		setTimeout(() => this.showCloseButton = true, 400);

		this.eventsService.subscribe("events:social:auth:error", (data) => {
			this.error = data;
			if(data.error_code === "CUSTOMER_EXCLUDED_BY_AUTHORITY"){
				this.error.setErrorMessage("Unfortunately, there is an error - you can’t play on this site.")
			}
		});
	}

	emailOrBoydCardNumberValidator(): ValidatorFn {
		return (control) => {
			const value = control.value;
			this.maxEmailCharacters = 250;
			const maxUsernameCharacters = 50
			if (value) {
				if (/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(value) && value.length <= this.maxEmailCharacters) {
					return null; // Valid email
				} else if (/^[A-Za-z0-9]{11}$/.test(value)) {
					return null; // valid boyd card number
				} else if (value.length <= maxUsernameCharacters) {
					return null; // valid username check
				} else {
					return { invalidInput: true }; // Invalid input
				}
			}
			return null;
		};
	}

	ngOnDestroy() {
		this.biometricService.verificationError = null;
		this.error = null;
		this.eventsService.unsubscribe("events:social:auth:error");
	}

	async initiateBiometric() {
		this.biometricService.verificationError = null;
		await this.biometricService.isAvailable();
		await this.biometricService.verifyUser();
		this.doLoginBiometric()
	}

	generateCsrfToken() {
		this.authenticationService.getCsrfToken().subscribe((response) => {
			if (!!response.payload && !!response.payload.token) {
				this.loginForm.controls['csrf'].setValue(response.payload.token);
			}
		});
	}

	async isFirstTimeAppLaunch() {
		this.platformID = (await this.appStorageService.getPlayerPlatformID()).value;
		console.log(this.platformID)
		if (this.platformID !== null) {
			this.isPlayerFirstVisit = false;
		} else {
			this.isPlayerFirstVisit = true;
		}
		//Also Check for Guest User logic
		this.appStorageService.getPlayerAsGuest().then((isGuest) => {
			if (isGuest.value === 'true') return this.isPlayerLoggedInAsGuest = true;
			return this.isPlayerLoggedInAsGuest = false;
		})
	}

	ionViewDidEnter() {
		this.successMessageService.clearMessage();
	}

	async doLogin() {
		this.buttonDisabled = true;
		this.reverseTile();
		this.doLoginInProgress = true;
		this.error = null;

		this.email = this.loginForm.get('username').value.trim();

		if (!this.loginForm.valid) return console.log('Form validation failed');

		this.loginForm.get("clientapp").setValue(this.configService.getClientApp());
		const value = this.loginForm.value;
		const device = this.uniqueID.getMFADeviceId();
		value.device_id = device;
		let fbp = Cookies.get('_fbp');
		let fbc = Cookies.get('_fbc');

		if (typeof fbp !== 'undefined') {
			value.deviceDetails = fbp;
		}
		if (typeof fbc !== 'undefined') {
			value.externalSource = fbc;
		}
		const apiCall$ = this.loginService.loginCustomer(value);
		this.progressingUI.listenObservable(apiCall$);
		apiCall$.subscribe(async (response: any) => {


			if (response.result === 0) return this.doLoginFailProtocol(response.code);

			this.userSessionService.setSessionToken(response.sessionToken);

			//Special Case to set platformID if user has account and use different device to login
			if (!isUndefined(response.platformId)) {
				this.appStorageService.setPlayerPlatformID(response.platformId);
				this.appStorageService.setPlayerAsGuest(false);
			}
			this.appStorageService.setPlayerAsGuest(false);

			if (this.biometricService.useBiometric) {
				this.biometricService.setCredentials(value);
			}
			localStorage.setItem('logininfo', JSON.stringify(value));

			this.loginService.setLoginResponseInStorage().then(() => {
				this.galleryService.getGameList()
			});
			this.doLoginInProgress = false;
		},
			(error) => {
				this.error = error;
				this.doLoginFailProtocol(error.getErrorCode().toUpperCase());
				this.buttonDisabled = false;
			});
	}

	doLoginFailProtocol(errorCode: string) {
		this.generateCsrfToken();
		this.content.scrollToTop(10);
		if (!this.error || !(this.error instanceof GlobalErrorHandlerService)) {
			this.error = this.globalErrorHandlerService;
		}

		switch (errorCode) {
			case 'LOGIN_WRONG_USERNAME':
				this.error.setErrorMessage('Sorry, an error has occurred. Please try again.');
				this.username.setErrors({ LOGIN_WRONG_USERNAME: true });
				this.username.markAsDirty();
				this.username.markAsTouched();
				break;
			case 'COGNITO_USER_NOTFOUND_ERROR':
				this.error.setErrorMessage('An error has occured logging you in. Please try again.');
				this.username.setErrors({ COGNITO_USER_NOTFOUND_ERROR: true });
				this.username.markAsDirty();
				this.username.markAsTouched();
				break;
			case 'LOGIN_WRONG_PASSWORD':
				this.error.setErrorMessage('Sorry, an error has occurred. Please try again.');
				this.password.setErrors({ LOGIN_WRONG_PASSWORD: true });
				this.password.markAsDirty();
				this.password.markAsTouched();
				break;
			case 'FORCE_PASSWORD_RESET':
				this.error.setErrorMessage("An e-mail has been sent to you. Please follow the instructions to reset your password.");
				this.startMigrationProcess();
				break;
			case 'FAILED_TOO_MANY_ATTEMPTS':
				this.error.setErrorMessage("Too many Attemps, please contact support");
				break;
			case "LOGIN_MAX_LOGINS_REACHED":
				this.error.setErrorMessage("Your account was disabled upon too many failed logins, please contact support.");
				this.tooManyLoginAttemps = true;
				this.checkUserNameFieldValue();
				break;
			case 'TIME_LIMIT_EXCEEDED':
				this.error.setErrorMessage('Time limit exceeded.');
				break;
			case 'NOT_AUTHORIZED_CLIENT_APP':
				this.error.setErrorMessage('You are logging from an unauthorized client app version.');
				break;
			case 'CUSTOMER_EXCLUDED_BY_AUTHORITY':
				this.error.setErrorMessage('Unfortunately, there is an error - you can’t play on this site.');
				break;
			case 'CUSTOMER_WRONG_STATUS':
				this.error.setErrorMessage("Your account is not in 'Active' status. Please contact support.");
				break;
			case 'CUSTOMER_SELF_EXCLUDED':
				this.navigator.toLobby();
				this.navigator.openPopover("PopoverSelfExcludeComponent", "", 'standard-popover');
				break;
			case 'CUSTOMER_SELF_EXCLUDED_COOL_OFF':
				this.error.setErrorMessage('You have suspended yourself from play.');
				break;
			case 'CUSTOMER_SUSPENDED':
				this.error.setErrorMessage('Your account has been temporarily suspended. If this is not resolved in 24 hours please contact support.');
				break;
			case 'EMAIL_ALREADY_EXISTS':
				this.error.setErrorMessage((!!this.error.getPayload()) ? this.error.getPayload() : "Email already exists");
				break;
			case 'USER_NOTFOUND_ERROR':
				this.error.setErrorMessage((!!this.error.getPayload()) ? this.error.getPayload() : "Player has not been found.");
				break;
			default:
				this.error.setErrorMessage('An error has occured logging you in. Please try again.');
				break;
		}
		this.buttonDisabled = false;
		this.eventsService.publish('event:login:error', { errorCode: this.error.getErrorCode().toUpperCase(), errorName: this.error.getErrorMessage() });
	}

	public startMigrationProcess(): any {
		this.reverseTile();
		let clientApp = this.configService.getClientApp();
		this.authenticationService.callForgotPasswordApi(this.email, clientApp).subscribe((response: any) => {
			if (!!response) this.showPasswordResetAlert();
		}, error => {
			this.content.scrollToTop(10);
			this.error = error;
			console.error(this.error);
		});
	}

	public showPasswordResetAlert(): any {
		this.reverseTile();
		const popoverComp = 'PopoverAlertComponent';

		let data: any = {
			content: "An e-mail has been sent to you. Please follow the instructions to reset your password.",
		};

		return this.navigator.openPopover(popoverComp, data);
	}

	async toggleBiometric() {
		await this.biometricService.onToggleUseBiometric()
		if (this.biometricService.useBiometric) this.doLoginBiometric()
	}

	async doLoginBiometric() {
		this.reverseTile();
		if (this.biometricService.verificationError) {
			//verfication failed
			await this.alertCreator(
				{
					header: `${this.biometricService.biometricType} verification failed`,
					message: 'Would you like to remove your saved credentials?',
					buttons: [
						{
							text: 'Yes',
							handler: async () => {
								await this.biometricService.deleteCredentials();
								await this.biometricService.setBiometricStatus(false);
							},
						},
						{
							text: 'No',
							handler: async () => {
								await this.biometricService.setBiometricStatus(true);
								this.biometricService.verificationError = null;
							},
						},
					],
				}
			)
		}
		if (this.biometricService.verified) {
			this.loginForm.controls['password'].setValue(this.biometricService.credentials.password);
			this.loginForm.controls['username'].setValue(this.biometricService.credentials.username);
			this.biometricService.verified = false;
			this.doLogin()
		}
	}

	public navigateTo(page: string, customModalClass?: string, animated?: boolean): any {
		this.reverseTile();
		return this.navigator.navigateTo(page, { customModalClass: customModalClass, animated: animated });
	}

	public hidePassword(): boolean {
		this.showPassword = false;
		return;
	}

	public unhidePassword(): boolean {
		this.showPassword = true;
		return;
	}

	async alertCreator(alert) {
		const currentAlert = await this.alertController.create({
			header: alert.header,
			message: alert.message,
			buttons: alert.buttons
		});
		await currentAlert.dismiss();
		await currentAlert.present();
	}

	public checkUserNameFieldValue() {
		const value = this.loginForm.get('username').value;
		if (/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(value) && value.length <= this.maxEmailCharacters) {
			this.userEnteredEmail = true;
		} else {
			console.log('is it boyd')
			this.userEnteredBoydCard = true;
		}
	}

	public onSendResetPasswordEMail(email): void {
		this.btnSendEmailDisabled = true;
		let clientApp = this.configService.getClientApp();
		let obj = {
			email: email,
			clientapp: clientApp
		}

		//this.authenticationService.callForgotPassword(obj).subscribe((response: any) => this.emailisSent = true, (error) => this.emailisSent = true);
		if (/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(this.username.value) && this.username.value.length <= this.maxEmailCharacters) {
			const apiCall$ = this.authenticationService.callForgotPassword(obj);
			this.progressingUI.listenObservable(apiCall$);
			apiCall$.subscribe(
				(response: any) => {
					this.emailisSent = true;
				},
				(error) => {
					this.emailisSent = true;
				}
			);
		}
		//Boyd Card Check - TO DO
		if (/^[A-Za-z0-9]{11}$/.test(this.username.value)) {
			const apiCall$ = this.rewardService.callIsRewardCardRegistered(this.username.value);
			this.progressingUI.listenObservable(apiCall$);
			apiCall$.subscribe(res => {
				//this.navigator.dismiss();
				if (res.payload === true) {
					this.showCardNotFound = false;
					this.navigator.navigateTo('ResetBoydPasswordComponent', { customModalClass: "crown-modal" });
				} else {
					this.navigator.navigateTo('ResetBoydPasswordComponent', { customModalClass: "crown-modal" });
				}
			}, (error) => {
				this.navigator.navigateTo('ResetBoydPasswordComponent', { customModalClass: "crown-modal" });
			})
		}
	}

	goToBoyd() {
		this.appLinksService.openExternalPage(this.boydLoginURL);
	}


	iconHoverEndEmitterFit() {
		let hoverItem = document.getElementsByClassName('message-box-visible');
		for (var i = 0; i < hoverItem.length; i++) {
			hoverItem[i].className = hoverItem[i].className.replace('message-box-visible', 'message-box');
		}
	}

	openMatchedExternalLinks(code, title) {
		this.reverseTile();
		return this.appLinksService.openMatchedExternalLinksPopover(code, title)
	}

	onLogoTap() {
		if (this.platform.is('capacitor')) {
			this.tapCount += 1;
			clearTimeout(this.tapTimeoutID);
			if (this.tapCount >= 10) {
				this.showDeviceId = true
			}
			else {
				this.tapTimeoutID = setTimeout(() => {
					this.tapCount = 0;
				}, 5000)
			}
		}
	}

	getDeviceId() {
		if (this.platform.is('capacitor')) {
			this.uniqueID.getSelectorID().then(
				response => {
					this.deviceId = response
				},
				reject => {
					this.deviceId = "Not available!"
				}
			);
		}
	}

	goToSDSCForgetPassword() {
		this.reverseTile();
		return this.navigator.navigateTo('ForgotPasswordComponent', { backdropDismiss: true, customModalClass: 'crown-modal' });
	}

	onClose = () => {
		this.reverseTile();
		this.navigator.toLobby();
	}

	onBack = () => {
		this.reverseTile();
		this.navigator.back();
	}

	private reverseTile(): any {
		return this.events.publish("events:animation:tile:reverse");
	}


	private animateComponent() {
		setTimeout(() => {
			const element = document.querySelector('.login-animator');
			if (element) {
				gsap.set(element, { rotationY: -90 });

				gsap.to(element, {
					rotationY: 0,
					delay: 0.1,
					duration: 0.3,
					ease: 'power2.inOut',
				});
			}
		});
	}

	protected appleLogin(): any {
		return this.socialAuth.appleLogin(this.isPlayerLoggedInAsGuest);
	}

	protected fbLogin(): any {
		return this.socialAuth.fbLogin(this.isPlayerLoggedInAsGuest);
	}

	protected googleLogin(): any {
		return this.socialAuth.googleLogin(this.isPlayerLoggedInAsGuest);
	}

}
